import { decryptDataRequest } from '../firebaseConfig/cloudFunctions.js';

/**
 * This queuing system is designed to significantly reduce the number
 * of unique decryption cloud requests, while interfacing
 * uniquely between each request. Upon each execution, it will wait a
 * constant duration for any requests to come in, parse and resolve
 * data, and manage the queue in an isolated environment.
 */
const WAIT_TIME = 0;
const CHUNK_SIZE = 50;
const decryptionQueue = {
    queue: [],
    request: decryptDataRequest,
};
const executeRequestQueue = async (backupLength) => {
    // Verify all tasks have been added to this batch session
    const { queue, request } = decryptionQueue;
    const currentBatchLength = queue.length;
    if (backupLength !== currentBatchLength)
        return;
    const splitQueues = [];
    const splitLength = Math.ceil(currentBatchLength / CHUNK_SIZE);
    Array(splitLength).fill(0).forEach((_, i) => {
        const prevSplitIndex = CHUNK_SIZE * i;
        let splitIndex = CHUNK_SIZE * (i + 1);
        const isLastChunk = i === (splitLength - 1);
        if (isLastChunk)
            splitIndex = undefined;
        splitQueues.push(queue.slice(prevSplitIndex, splitIndex));
    });
    const suffix = splitLength === 1 ? '' : 's';
    console.info(`executing ${currentBatchLength} decryption tasks in ${splitLength} chunk${suffix}...`);
    // Begin execution
    decryptionQueue.queue = [];
    const promises = splitQueues.map(async (subQueue) => {
        const executionQueue = [...subQueue];
        const data = executionQueue.map((val) => val.encrypted);
        const allResponseData = await request({ data });
        // Resolve promises of each unique request
        allResponseData.forEach((result, index) => {
            const queueTask = executionQueue[index];
            if (!queueTask)
                throw new Error('Missing queue task.');
            queueTask.resolve(result);
        });
    });
    await Promise.all(promises);
};
/**
 * Add encrypted data to the existing (if any) request batch
 * @param encrypted Encrypted data to decrypt
 * @return {Promise<RawData<any>>} Decrypted raw data
 */
const addToDecryptionBatch = (encrypted) => new Promise((resolve) => {
    decryptionQueue.queue.push({ encrypted, resolve });
    const previousLength = decryptionQueue.queue.length.valueOf();
    setTimeout(() => executeRequestQueue(previousLength), WAIT_TIME);
});

export { addToDecryptionBatch };
